import React, { useState, useEffect, useRef } from "react";
import posthog from 'posthog-js';
import SparksAddyBurst from '../../Sparks/SparksAddyBurst';
import AddyAvatar from '../../Sparks/AddyAvatar';
import useCosmeticCatalogue from '../../Sparks/useCosmeticCatalogue';
import { CELEBRATION_MS } from '../../Sparks/cosmetics';
import { useSparks } from '../../../contexts/SparksContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useHideAskAddy } from '../../../contexts/NavVisibilityContext';
import "./QuestionPage.css";

import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/shadcn/card';

import yay from "../../../media/streaks/yay.mp3";
import Burning2 from "../../../media/streaks/Burning-2.gif";
import Burning3 from "../../../media/streaks/Burning-3.gif";
import Burning4 from "../../../media/streaks/Burning-4.gif";
import Burning5 from "../../../media/streaks/Burning-5.gif";
import Burning6 from "../../../media/streaks/Burning-6.gif";
import Burning7 from "../../../media/streaks/Burning-7.gif";
import Burning8 from "../../../media/streaks/Burning-8.gif";
import Burning9 from "../../../media/streaks/Burning-9.gif";
import Burning10 from "../../../media/streaks/Burning-10.gif";
import Burning11 from "../../../media/streaks/Burning-11.gif";
import Burning12 from "../../../media/streaks/Burning-12.gif";
import Burning13 from "../../../media/streaks/Burning-13.gif";
import Burning14 from "../../../media/streaks/Burning-14.gif";
import Burning15 from "../../../media/streaks/Burning-15.gif";
import Burning16 from "../../../media/streaks/Burning-16.gif";
import Burning17 from "../../../media/streaks/Burning-17.gif";
import Burning18 from "../../../media/streaks/Burning-18.gif";
import Burning19 from "../../../media/streaks/Burning-19.gif";
import Burning20 from "../../../media/streaks/Burning-20.gif";

import incorrectQuizSound from "../../../media/incorrect-quiz-sound.mp3";
import correctSound from "../../../media/correct-answer-sound.mp3";
import audioManager from '../../../utils/audioManager';
import {
    isSlotCorrectInContext,
    isBlankBoxCorrectInContext,
    getSlotLiveStatus,
    getBlankBoxLiveStatus,
} from '../../../utils/quizResultsUtils';
import ProgressBar from "../../ProgressBar/ProgressBar";

const burningAnimations = {
    2: Burning2,
    3: Burning3,
    4: Burning4,
    5: Burning5,
    6: Burning6,
    7: Burning7,
    8: Burning8,
    9: Burning9,
    10: Burning10,
    11: Burning11,
    12: Burning12,
    13: Burning13,
    14: Burning14,
    15: Burning15,
    16: Burning16,
    17: Burning17,
    18: Burning18,
    19: Burning19,
    20: Burning20
};

function QuestionPage({ 
    allQuizQuestionsLength, 
    outstandingQuestionsLength,
    currentQuestionNumber,
    setCurrentQuestionIndex, 
    quizQuestion, 
    onAnswerSelect, 
    ButtonsAreClickable, 
    setButtonsAreClickable, 
    setQuizIsFinished,
    onInputChange,
    isExplanationQuestion = false,
    showNextButton = false,
    showFeedback = true,
    onNextClick = () => {},
    resetFeedback = false,
    hideBlankBoxCorrectnessIndicators = false,
    // Draw Sparks bubbles from Addy + the corner balance (student quizzes only; never in the tutor preview).
    showSparks = false
}) {
    const submitInProgressRef = useRef(false);   // blocks double-submit of answers
    const addyRef = useRef(null);                // Sparks bubbles spawn at Addy's head
    const navigationInProgressRef = useRef(false); // blocks double-click of Next

    const [selectedAnswer, setSelectedAnswer] = useState("select");
    const [FreeTextInputValue, setFreeTextInputValue] = useState("");
    // The child's customised Addy (none in the tutor preview, which shows the default Addy).
    const sparks = useSparks();
    const { loggedInChild } = useAuth();
    const cosmeticCatalogue = useCosmeticCatalogue(showSparks ? loggedInChild?._id : null);
    const [addyTalking, setAddyTalking] = useState(false);
    const [addyCelebrating, setAddyCelebrating] = useState(false);
    const celebrateTimerRef = useRef(null);
    useHideAskAddy();
    useEffect(() => () => clearTimeout(celebrateTimerRef.current), []);
    //const [NextButton, setNextButton] = useState(false); (state now redundant)
    const [correctFeedback, setCorrectFeedback] = useState(false);
    const [incorrectFeedback, setIncorrectFeedback] = useState(false);
    const [remainingQuestionsLength, setRemainingQuestionsLength] = useState(outstandingQuestionsLength);
    const [streak, setStreak] = useState(0);
    const [streakAnimation, setStreakAnimation] = useState(null);
    const [freeTextError, setFreeTextError] = useState(false);
    const [blankBoxError, setBlankBoxError] = useState(false);

    const [blankBoxAnswers, setBlankBoxAnswers] = useState({});
    const [currentBoxIndex, setCurrentBoxIndex] = useState(0);
    const [droppedLabels, setDroppedLabels] = useState({});
    const [shuffledLabels, setShuffledLabels] = useState([]);
    const [nextBoxId, setNextBoxId] = useState(null);

    // Mixed question: per-slot answers { [slotId]: string }
    const [mixedSlotAnswers, setMixedSlotAnswers] = useState({});
    const [mixedError, setMixedError] = useState(false);
    const [nextMixedDragSlotId, setNextMixedDragSlotId] = useState(null);

    const correctAnswerSound = { play: () => audioManager.play(correctSound, 1) };
    const yaySound           = { play: () => audioManager.play(yay, 1) };
    const incorrectSound     = { play: () => audioManager.play(incorrectQuizSound, 1) };

    const getStreakAnimation = (streak) => {
        if (streak < 2) return null;
        if (streak > 20) return burningAnimations[20];
        return burningAnimations[streak];
    };

    // Centralised correct-answer animation. Called inside setStreak's updater so
    // newStreak is the already-incremented value. Every correct answer gets the same
    // move: the celebration the child equipped in the shop. Every 5 in a row also cheers "yay".
    const applyCorrectAnswerAnimation = (newStreak) => {
        setStreakAnimation(getStreakAnimation(newStreak));
        if (newStreak % 5 === 0) yaySound.play();
        setAddyTalking(false);
        setAddyCelebrating(true);
        clearTimeout(celebrateTimerRef.current);
        celebrateTimerRef.current = setTimeout(() => setAddyCelebrating(false), CELEBRATION_MS);
    };

    const handleNextClick = () => {
        if (navigationInProgressRef.current) {
            posthog.capture('next_button_blocked', {
                questionId: quizQuestion?._id,
                questionType: quizQuestion?.questionType,
                questionIndex: currentQuestionNumber,
            });
            return;
        }
        navigationInProgressRef.current = true;
        // Forensic marker for every successful advance. Pairs with
        // next_button_blocked so a replay distinguishes "clicked Next →
        // advanced" from "clicked Next → blocked no-op (child was early)".
        posthog.capture('next_question_clicked', {
            questionId: quizQuestion?._id,
            questionType: quizQuestion?.questionType,
            questionIndex: currentQuestionNumber,
            remainingQuestions: remainingQuestionsLength,
        });
        // Call the onNextClick callback to allow parent to reset explanation state
        onNextClick();
        
        if (remainingQuestionsLength <= 1 ) {
            setQuizIsFinished(true);
        }
        setCurrentQuestionIndex(currentIndex => currentIndex + 1);
        //setNextButton(false); (now redundant)
        setButtonsAreClickable(true);
        setCorrectFeedback(false);
        setIncorrectFeedback(false);
        setRemainingQuestionsLength(remainingQuestionsLength => remainingQuestionsLength - 1);

        // now reset all of the quiz entry states
        setSelectedAnswer("select");
        setFreeTextInputValue("");
        setBlankBoxAnswers({});
        setDroppedLabels({});
        setMixedSlotAnswers({});
        setMixedError(false);
        setStreakAnimation(getStreakAnimation(streak));
        setFreeTextError(false);
        setBlankBoxError(false);
    };

    // Reset feedback when resetFeedback changes or when isExplanationQuestion changes
    useEffect(() => {
        if (resetFeedback || isExplanationQuestion) {
            setCorrectFeedback(false);
            setIncorrectFeedback(false);
        }
    }, [resetFeedback, isExplanationQuestion]);

    // Reset input fields when the question changes or when switching to an explanation question.
    // IMPORTANT: dep is quizQuestion?._id, not the full quizQuestion object.
    // The full object reference changes on every parent re-render (Quiz.js re-renders when
    // LearningSession broadcasts state), which previously caused this effect to fire mid-keystroke
    // and wipe the student's typed answer. Keying on _id ensures the reset only runs when the
    // question genuinely changes.
    // If quizQuestion is ever restructured so that _id is not the stable unique identifier
    // (e.g. synthetic explanation questions built inline without a real _id), this guard
    // will need revisiting — two different questions with the same or missing _id would
    // prevent the reset from firing between them.
    useEffect(() => {
        // Reset guards so the new question can be submitted and navigated
        submitInProgressRef.current = false;
        navigationInProgressRef.current = false;

        // Reset all input fields
        setSelectedAnswer("select");
        setFreeTextInputValue("");
        setBlankBoxAnswers({});
        setDroppedLabels({});
        setMixedSlotAnswers({});
        setMixedError(false);
        setFreeTextError(false);
        setBlankBoxError(false);

        const getMixedPoolLabels = () => {
            if (Array.isArray(quizQuestion?.dragPoolLabels) && quizQuestion.dragPoolLabels.length > 0) {
                return quizQuestion.dragPoolLabels;
            }
            return (quizQuestion?.slots || [])
                .filter((s) => s.slotType === 'drag-and-drop')
                .map((s) => s.label)
                .filter(Boolean);
        };

        // Reset any labels for drag and drop questions
        if (quizQuestion?.questionType === 'Drag-And-Drop-Question') {
            const labels = quizQuestion.dragBoxes.map(box => box.label);
            setShuffledLabels([...labels].sort(() => Math.random() - 0.5));
            const sorted = quizQuestion.dragBoxes.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            const firstBox = sorted[0];
            if (firstBox) {
                setNextBoxId(firstBox.id);
            }
        } else if (quizQuestion?.questionType === 'Mixed') {
            const pool = getMixedPoolLabels();
            setShuffledLabels([...pool].sort(() => Math.random() - 0.5));
            const firstDrag = (quizQuestion.slots || []).filter(s => s.slotType === 'drag-and-drop').sort((a, b) => (a.order || 0) - (b.order || 0))[0];
            setNextMixedDragSlotId(firstDrag?.id ?? null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quizQuestion?._id, isExplanationQuestion]); // _id guards against object reference churn; full quizQuestion still read in body via closure

    const handleButtonClick = (answer) => {
        setSelectedAnswer(answer.id);
        
        // Send the input update to the tutor
        onInputChange?.({
            questionType: 'Multiple-Choice-Question',
            value: answer
        });

        const isCorrect = answer.id === quizQuestion.correctAnswer;
        const hasExplanation = !isCorrect && quizQuestion.answerExplanationQuestionId;

        // Pre-submit selection marker. Multiple-choice auto-submits, so this
        // fires alongside answer_submitted — it names the discrete "selected"
        // interaction. Typed/drag selection is continuous and left to replay
        // to avoid per-keystroke noise.
        posthog.capture('quiz_answer_selected', {
            questionId: quizQuestion?._id,
            questionType: quizQuestion?.questionType,
            questionIndex: currentQuestionNumber,
            isCorrect,
        });

        if (isCorrect) {
            setCorrectFeedback(true);
            correctAnswerSound.play();
            setStreak(prevStreak => {
                const newStreak = prevStreak + 1;
                applyCorrectAnswerAnimation(newStreak);
                return newStreak;
            });
        } else {
            playIncorrectFeedback(hasExplanation);
        }

        onAnswerSelect(isCorrect, answer.id, null);
    };

    // Add handler for real-time text input changes
    const handleFreeTextChange = (e) => {
        const value = e.target.value;
        setFreeTextInputValue(value);
        
        // Send the input update to the tutor as the student types
        onInputChange?.({
            questionType: 'Free-Text-Question',
            value: value
        });
    };

    const handleFreeTextSubmit = (inputValue) => {
        // Check if answer is empty or just whitespace
        if (!inputValue.trim()) {
            setFreeTextError(true);
            return; // Stop execution if answer is empty
        }
        
        // Clear any previous error
        setFreeTextError(false);

        // Send the input update to the tutor
        const lowerCaseTexts = quizQuestion.answers.map(answer => answer.text.toLowerCase().trim());
        const isCorrect = lowerCaseTexts.includes(inputValue.toLowerCase().trim());
        const hasExplanation = !isCorrect && quizQuestion.answerExplanationQuestionId;

        onInputChange?.({
            questionType: 'Free-Text-Question',
            value: inputValue,
            isSubmitted: true,
            isCorrect: isCorrect
        });

        if (isCorrect) {
            setCorrectFeedback(true);
            correctAnswerSound.play();
            setStreak(prevStreak => {
                const newStreak = prevStreak + 1;
                applyCorrectAnswerAnimation(newStreak);
                return newStreak;
            });
        } else {
            playIncorrectFeedback(hasExplanation);
        }

        onAnswerSelect(isCorrect, inputValue, null);
    };

    const handleDropDownSubmit = () => {

        if (!quizQuestion.answers.some(answer => answer.id === selectedAnswer)){
            // Don't submit if "Select Answer" is chosen
            return;
          }

        const isCorrect = (selectedAnswer === quizQuestion.correctAnswer);
        const hasExplanation = !isCorrect && quizQuestion.answerExplanationQuestionId;

        // Send the input update to the tutor
        onInputChange?.({
            questionType: 'Drop-Down-List',
            value: selectedAnswer,
            isSubmitted: true,
            isCorrect: isCorrect
        });

        if (isCorrect) {
            setCorrectFeedback(true);
            correctAnswerSound.play();
            setStreak(prevStreak => {
                const newStreak = prevStreak + 1;
                applyCorrectAnswerAnimation(newStreak);
                return newStreak;
            });
        } else {
            playIncorrectFeedback(hasExplanation);
        }

        onAnswerSelect(isCorrect, selectedAnswer, null);
    };

    const ButtonColour = (selectedAnswer, answer) => {
        let buttonClass = 'min-h-12 px-2 py-3 my-2 text-lg text-black border border-2 border-black rounded-lg focus:outline-none flex items-center justify-center text-center';

        if (selectedAnswer === answer.id && answer.id === quizQuestion.correctAnswer) {
            buttonClass += ' correct';
        } else if (selectedAnswer === answer.id && answer.id !== quizQuestion.correctAnswer) {
            buttonClass += ' incorrect';
        } else {
            buttonClass += ' bg-white';
        }

        return buttonClass;
    };


    //-----------------------------------
    // Blank Box Functions
    //-----------------------------------

    const handleBlankBoxInput = (boxId, value) => {
        const updatedAnswers = {
            ...blankBoxAnswers,
            [boxId]: value
        };
        setBlankBoxAnswers(updatedAnswers);

        // Send the input update to the tutor
        onInputChange?.({
            questionType: 'Blank-Box-Question',
            value: updatedAnswers
        });
    };

    const handleBlankBoxKeyPress = (e, currentBox) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const nextBox = quizQuestion.blankBoxes.find(box => box.order === currentBox.order + 1);
            if (nextBox) {
                setCurrentBoxIndex(currentBox.order);
                document.getElementById(`box-${nextBox.id}`).focus();
            } else {
                handleBlankBoxSubmit();
            }
        }
    };

    const handleBlankBoxSubmit = () => {
        if (submitInProgressRef.current) return;

        // Check if any box is empty
        const anyEmptyBox = quizQuestion.blankBoxes.some(box =>
            !blankBoxAnswers[box.id] || blankBoxAnswers[box.id].trim() === ''
        );

        if (anyEmptyBox) {
            setBlankBoxError(true);
            return; // Stop execution if any box is empty
        }

        submitInProgressRef.current = true;

        // Clear any previous error
        setBlankBoxError(false);
        
        // Check if each answer is correct. Link-group aware: boxes sharing a
        // linkGroupId are judged by multiset equality, so commutative answers
        // (e.g. 19*5 and 5*19) both pass.
        const isCorrect = quizQuestion.blankBoxes.every(box =>
            isBlankBoxCorrectInContext(box, quizQuestion.blankBoxes, blankBoxAnswers)
        );

        const hasExplanation = !isCorrect && quizQuestion.answerExplanationQuestionId;

        // Send the input update with isSubmitted flag
        onInputChange?.({
            questionType: 'Blank-Box-Question',
            value: blankBoxAnswers,
            isSubmitted: true,
            isCorrect: isCorrect
        });

        if (isCorrect) {
            setCorrectFeedback(true);
            correctAnswerSound.play();
            setStreak(prevStreak => {
                const newStreak = prevStreak + 1;
                applyCorrectAnswerAnimation(newStreak);
                return newStreak;
            });
        } else {
            playIncorrectFeedback(hasExplanation);
        }

        onAnswerSelect(isCorrect, 'N/A', blankBoxAnswers);
    };

    //-----------------------------------
    // Drag Box Functions
    //-----------------------------------
    const removeOneLabel = (labels, target) => {
        const idx = labels.indexOf(target);
        if (idx === -1) return labels;
        return [...labels.slice(0, idx), ...labels.slice(idx + 1)];
    };

    const handleLabelClick = (label) => {
        if (!ButtonsAreClickable) return;
        
        // Find the next empty box by order
        const filledBoxIds = Object.keys(droppedLabels);
        const nextBox = quizQuestion.dragBoxes
            .filter(box => !filledBoxIds.includes(box.id))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];

        if (nextBox) {
            const updatedLabels = {
                ...droppedLabels,
                [nextBox.id]: label
            };
            
            // Update droppedLabels with the new label
            setDroppedLabels(updatedLabels);

            // Send the input update to the tutor
            onInputChange?.({
                questionType: 'Drag-And-Drop-Question',
                value: updatedLabels
            });

            // Update shuffledLabels
            setShuffledLabels(prev => removeOneLabel(prev, label));

            // Set next box to highlight
            const nextEmptyBox = quizQuestion.dragBoxes
                .filter(box => !filledBoxIds.includes(box.id) && box.id !== nextBox.id)
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];
            setNextBoxId(nextEmptyBox?.id || null);
        }
    };

    const handleDragBoxSubmit = () => {
        const isCorrect = quizQuestion.dragBoxes.every(box => 
            droppedLabels[box.id] === box.label
        );

        const hasExplanation = !isCorrect && quizQuestion.answerExplanationQuestionId;

        // Send the input update with isSubmitted flag
        onInputChange?.({
            questionType: 'Drag-And-Drop-Question',
            value: droppedLabels,
            isSubmitted: true,
            isCorrect: isCorrect
        });
        
        if (isCorrect) {
            setCorrectFeedback(true);
            correctAnswerSound.play();
            setStreak(prevStreak => {
                const newStreak = prevStreak + 1;
                applyCorrectAnswerAnimation(newStreak);
                return newStreak;
            });
        } else {
            playIncorrectFeedback(hasExplanation);
        }

        onAnswerSelect(isCorrect, 'N/A', droppedLabels);
    };

    // -----------------------------------
    // Mixed question handlers
    // -----------------------------------
    const slots = (quizQuestion.slots || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    const mixedDragSlots = slots.filter(s => s.slotType === 'drag-and-drop');
    const hasPosition = (s) => s.x != null && s.y != null && s.width != null && s.height != null;

    const handleMixedSlotChange = (slotId, value) => {
        setMixedSlotAnswers(prev => ({ ...prev, [slotId]: value }));
        onInputChange?.({ questionType: 'Mixed', value: { ...mixedSlotAnswers, [slotId]: value } });
    };

    const handleMixedLabelClick = (label) => {
        if (!ButtonsAreClickable || !nextMixedDragSlotId) return;
        const filledIds = Object.keys(mixedSlotAnswers).filter(id => {
            const slot = slots.find(s => s.id === id);
            return slot?.slotType === 'drag-and-drop';
        });
        const nextSlot = mixedDragSlots.find(s => s.id === nextMixedDragSlotId) || mixedDragSlots.find(s => !filledIds.includes(s.id));
        if (!nextSlot) return;
        const updated = { ...mixedSlotAnswers, [nextSlot.id]: label };
        setMixedSlotAnswers(updated);
        setShuffledLabels(prev => removeOneLabel(prev, label));
        const after = mixedDragSlots.filter(s => s.id !== nextSlot.id && !filledIds.includes(s.id)).sort((a, b) => (a.order || 0) - (b.order || 0))[0];
        setNextMixedDragSlotId(after?.id ?? null);
        onInputChange?.({ questionType: 'Mixed', value: updated });
    };

    const handleMixedReset = () => {
        const pool = Array.isArray(quizQuestion.dragPoolLabels) && quizQuestion.dragPoolLabels.length > 0
            ? quizQuestion.dragPoolLabels
            : (quizQuestion.slots || [])
                .filter((s) => s.slotType === 'drag-and-drop')
                .map((s) => s.label)
                .filter(Boolean);
        setShuffledLabels([...pool].sort(() => Math.random() - 0.5));
        const withoutDrag = { ...mixedSlotAnswers };
        mixedDragSlots.forEach(s => delete withoutDrag[s.id]);
        setMixedSlotAnswers(withoutDrag);
        const first = mixedDragSlots[0];
        setNextMixedDragSlotId(first?.id ?? null);
    };

    const handleMixedSubmit = () => {
        const allSlots = slots.filter(hasPosition);
        const missing = allSlots.some(s => {
            const v = mixedSlotAnswers[s.id];
            return v === undefined || v === null || String(v).trim() === '';
        });
        if (missing) {
            setMixedError(true);
            return;
        }
        setMixedError(false);

        // Link-group aware per-slot correctness. Slots sharing a linkGroupId
        // are judged together as a multiset; unlinked slots use direct compare.
        const isCorrect = allSlots.every(slot =>
            isSlotCorrectInContext(slot, allSlots, mixedSlotAnswers)
        );

        const hasExplanation = !isCorrect && quizQuestion.answerExplanationQuestionId;
        onInputChange?.({ questionType: 'Mixed', value: mixedSlotAnswers, isSubmitted: true, isCorrect });

        if (isCorrect) {
            setCorrectFeedback(true);
            correctAnswerSound.play();
            setStreak(prevStreak => {
                const newStreak = prevStreak + 1;
                applyCorrectAnswerAnimation(newStreak);
                return newStreak;
            });
        } else {
            playIncorrectFeedback(hasExplanation);
        }
        onAnswerSelect(isCorrect, 'N/A', mixedSlotAnswers);
    };

    // Add handleReset function
    const handleReset = () => {
        // Reset droppedLabels to empty
        setDroppedLabels({});
        // Re-shuffle and restore all labels
        const labels = quizQuestion.dragBoxes.map(box => box.label);
        setShuffledLabels([...labels].sort(() => Math.random() - 0.5));
        const sorted = quizQuestion.dragBoxes.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const firstBox = sorted[0];
        if (firstBox) {
            setNextBoxId(firstBox.id);
        }
    };

    // Add a function to play the incorrect sound and show Addy talking animation
    const playIncorrectFeedback = (hasExplanation) => {
        setIncorrectFeedback(true);
        setStreak(0);
        setStreakAnimation(null);
        
        clearTimeout(celebrateTimerRef.current);
        setAddyCelebrating(false);
        // Addy only talks for explanation questions, until the sound ends
        if (hasExplanation) {
            incorrectSound.play();
            setAddyTalking(true);
            audioManager.onEnded(() => setAddyTalking(false));
        } else {
            setAddyTalking(false);
        }
    };

    // Homework hides green/red correctness styling while answering; it returns
    // during answer-explanation review just like other quiz types.
    const suppressLiveCorrectness = quizQuestion.isHomework && !quizQuestion.isAnswerExplanation;

    return (
        <div className="quiz-page-wrapper flex flex-col items-center w-full relative">
            {/* <div className="question-page-content-wrapper"> */}
                <ProgressBar 
                    currentQuestion={currentQuestionNumber ?? (allQuizQuestionsLength - remainingQuestionsLength + 1)}
                    totalQuestions={allQuizQuestionsLength}
                    labelText="Question"
                />
                
                <Card className="quiz-card w-full p-6 border-0 w-[80vw] max-w-4xl mx-auto max-md:w-full max-md:p-3">
                    {quizQuestion.image ? (
                        // Cases with image
                        <>
                           <div className="horizontal-center-wrapper">
                            <CardHeader className="quiz-card-header pb-4 rounded-2xl w-full mx-auto"> {/* border border-black*/}
                                <h2 className="question-page-title text-2xl md:text-3xl font-bold text-center">
                                    {quizQuestion.question}
                                </h2>
                            </CardHeader>
                            </div>
                            <div className="horizontal-center-wrapper">
                            <CardContent className="quiz-card flex justify-center rounded-2xl h-[65vh] w-full max-md:h-auto"> {/* border border-black*/}
                                {quizQuestion.questionType === 'Blank-Box-Question' ? (
                                    <div className="flex flex-col items-center">
                                        <div className="relative" style={{ display: 'inline-block', maxWidth: '100%' }}>
                                            <img 
                                                src={quizQuestion.image} 
                                                alt="Question" 
                                                className="object-contain max-h-[60vh] max-w-full"
                                                draggable="false"
                                            />
                                            {quizQuestion.blankBoxes.map((box) => (
                                                <div
                                                    key={box.id}
                                                    className="absolute question-page-blank-box-wrapper"
                                                    style={{
                                                        left: `${box.x}%`,
                                                        top: `${box.y}%`,
                                                        width: `${box.width}%`,
                                                        height: `${box.height}%`
                                                    }}
                                                >
                                                    {(() => {
                                                        const liveStatus = getBlankBoxLiveStatus(box, quizQuestion.blankBoxes, blankBoxAnswers);
                                                        const showIndicator = !hideBlankBoxCorrectnessIndicators && liveStatus !== 'neutral' && !suppressLiveCorrectness;
                                                        return (
                                                            <input
                                                                id={`box-${box.id}`}
                                                                type="text"
                                                                value={blankBoxAnswers[box.id] || ''}
                                                                onChange={(e) => handleBlankBoxInput(box.id, e.target.value)}
                                                                onKeyPress={(e) => handleBlankBoxKeyPress(e, box)}
                                                                autoComplete="off"
                                                                className={`w-full h-full p-1 border-2 question-page-blank-box-input ${
                                                                    showIndicator
                                                                        ? liveStatus === 'green'
                                                                            ? 'border-green-500 bg-green-100'
                                                                            : 'border-red-500 bg-red-100'
                                                                        : blankBoxError ? 'border-orange-500 bg-orange-200' : 'border-black bg-white'
                                                                }`}
                                                                disabled={!ButtonsAreClickable}
                                                            />
                                                        );
                                                    })()}
                                                </div>
                                            ))}
                                        </div>
                                        {!showNextButton && (
                                            <div className="mt-4 flex flex-col items-center">
                                                {blankBoxError && (
                                                    <div className="w-full text-center text-red-500 mt-1">
                                                        Please fill in all blank boxes before submitting.
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={handleBlankBoxSubmit}
                                                    className="quiz-submit-btn focus:outline-none"
                                                >
                                                    Submit
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) :
                                
                                quizQuestion.questionType === 'Drag-And-Drop-Question' ? (
                                   
                                    
                                        <div className="flex flex-col items-center">
                                            <div className="relative" style={{ display: 'inline-block', maxWidth: '100%' }}>
                                                <img 
                                                    src={quizQuestion.image} 
                                                    alt="Question" 
                                                    className="object-contain max-h-[60vh] max-w-full"
                                                    draggable="false"
                                                />
                                                {quizQuestion.dragBoxes.map((box) => (
                                                    <div
                                                        key={box.id}
                                                        className={`absolute question-page-drag-slot quiz-drop-zone ${droppedLabels[box.id] ? 'quiz-drop-zone-filled' : ''} ${
                                                            !ButtonsAreClickable
                                                                ? droppedLabels[box.id] === box.label
                                                                    ? '!border-nova-green !bg-nova-light-green'
                                                                    : '!border-nova-coral'
                                                                : box.id === nextBoxId && !droppedLabels[box.id]
                                                                ? '!border-nova-green !bg-nova-light-green'
                                                                : ''
                                                        }`}
                                                        style={{
                                                            left: `${box.x}%`,
                                                            top: `${box.y}%`,
                                                            width: `${box.width}%`,
                                                            height: `${box.height}%`
                                                        }}
                                                    >
                                                        {droppedLabels[box.id] ? (
                                                            <span className="quiz-drop-zone-label">{droppedLabels[box.id]}</span>
                                                        ) : null}
                                                    </div>
                                                ))}
                                            </div>
                                        
                                        
                                        <div className="flex max-md:flex-col max-md:items-center max-md:gap-3">
                                            <div className="flex flex-wrap justify-center gap-2">
                                                {shuffledLabels.map((label, index) => (
                                                    <div
                                                        key={index}
                                                        className={`quiz-chip ${Object.values(droppedLabels).includes(label) ? 'quiz-chip-used' : ''}`}
                                                        onClick={() => handleLabelClick(label)}
                                                    >
                                                        {label}
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="flex mx-2">
                                                {ButtonsAreClickable && Object.keys(droppedLabels).length > 0 && (
                                                    <button
                                                        onClick={handleReset}
                                                        type="button"
                                                        className="quiz-secondary-btn focus:outline-none"
                                                    >
                                                        Reset Labels
                                                    </button>
                                                )}
                                                {!showNextButton && shuffledLabels.length === 0 && (
                                                    <button
                                                        onClick={handleDragBoxSubmit}
                                                        type="button"
                                                        className="quiz-submit-btn focus:outline-none"
                                                    >
                                                        Submit
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                ) : quizQuestion.questionType === 'Mixed' ? (
                                    <div className="flex flex-col items-center">
                                        <div className="relative" style={{ display: 'inline-block', maxWidth: '100%' }}>
                                            <img
                                                src={quizQuestion.image}
                                                alt="Question"
                                                className="object-contain max-h-[60vh] max-w-full"
                                                draggable="false"
                                            />
                                            {slots.filter(hasPosition).map((slot) => (
                                                <div
                                                    key={slot.id}
                                                    className="absolute question-page-blank-box-wrapper"
                                                    style={{
                                                        left: `${slot.x}%`,
                                                        top: `${slot.y}%`,
                                                        width: `${slot.width}%`,
                                                        height: `${slot.height}%`
                                                    }}
                                                >
                                                    {slot.slotType === 'blank-box' && (() => {
                                                        const liveStatus = getSlotLiveStatus(slot, slots, mixedSlotAnswers);
                                                        const showIndicator = !suppressLiveCorrectness && liveStatus !== 'neutral';
                                                        return (
                                                            <input
                                                                type="text"
                                                                value={mixedSlotAnswers[slot.id] || ''}
                                                                onChange={(e) => handleMixedSlotChange(slot.id, e.target.value)}
                                                                disabled={!ButtonsAreClickable}
                                                                className={`w-full h-full p-1 border-2 text-sm max-md:text-base ${
                                                                    showIndicator
                                                                        ? liveStatus === 'green'
                                                                            ? 'border-green-500 bg-green-100'
                                                                            : 'border-red-500 bg-red-100'
                                                                        : mixedError ? 'border-orange-500 bg-orange-200' : 'border-gray-600 bg-white'
                                                                }`}
                                                            />
                                                        );
                                                    })()}
                                                    {slot.slotType === 'drag-and-drop' && (
                                                        <div
                                                            className={`w-full h-full flex items-center justify-center quiz-drop-zone ${mixedSlotAnswers[slot.id] ? 'quiz-drop-zone-filled' : ''} ${
                                                                !ButtonsAreClickable && mixedSlotAnswers[slot.id]
                                                                    ? mixedSlotAnswers[slot.id] === (slot.label || '')
                                                                        ? '!border-nova-green !bg-nova-light-green'
                                                                        : '!border-nova-coral'
                                                                    : slot.id === nextMixedDragSlotId && !mixedSlotAnswers[slot.id]
                                                                    ? '!border-nova-green !bg-nova-light-green'
                                                                    : ''
                                                            }`}
                                                        >
                                                            {mixedSlotAnswers[slot.id] ? (
                                                                <span className="quiz-drop-zone-label text-sm">{mixedSlotAnswers[slot.id]}</span>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                    {slot.slotType === 'multiple-choice' && (
                                                        <div className="w-full h-full overflow-auto p-1 flex flex-col gap-1">
                                                            {(slot.options || []).map((opt) => (
                                                                <button
                                                                    key={opt.id}
                                                                    type="button"
                                                                    onClick={() => ButtonsAreClickable && handleMixedSlotChange(slot.id, opt.id)}
                                                                    disabled={!ButtonsAreClickable}
                                                                    className={`quiz-option-card !min-h-9 py-1 ${(mixedSlotAnswers[slot.id] || '') === opt.id ? 'quiz-option-selected' : ''}`}
                                                                >
                                                                    <span className="quiz-option-dot !w-4 !h-4">
                                                                        {(mixedSlotAnswers[slot.id] || '') === opt.id && <span className="quiz-option-dot-inner !w-1.5 !h-1.5" />}
                                                                    </span>
                                                                    <span className="quiz-option-label text-sm">{opt.text}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {slot.slotType === 'dropdown' && (() => {
                                                        const liveStatus = getSlotLiveStatus(slot, slots, mixedSlotAnswers);
                                                        const effectiveStatus = suppressLiveCorrectness ? 'neutral' : liveStatus;
                                                        return (
                                                            <select
                                                                value={mixedSlotAnswers[slot.id] || ''}
                                                                onChange={(e) => handleMixedSlotChange(slot.id, e.target.value)}
                                                                disabled={!ButtonsAreClickable}
                                                                className={`w-full h-full border-2 text-xs p-1 max-md:text-base ${
                                                                    effectiveStatus === 'green'
                                                                        ? 'border-green-500 bg-green-100'
                                                                        : effectiveStatus === 'red'
                                                                            ? 'border-red-500 bg-red-100'
                                                                            : mixedError
                                                                                ? 'border-orange-500 bg-orange-200'
                                                                                : 'border-gray-400'
                                                                }`}
                                                            >
                                                                <option value="">Select...</option>
                                                                {(slot.options || []).map((opt) => (
                                                                    <option key={opt.id} value={opt.id}>{opt.text}</option>
                                                                ))}
                                                            </select>
                                                        );
                                                    })()}
                                                </div>
                                            ))}
                                        </div>
                                        {mixedDragSlots.length > 0 && (
                                            <div className="flex flex-wrap justify-center gap-2 mt-2">
                                                {shuffledLabels.map((label, idx) => (
                                                    <div
                                                        key={idx}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => handleMixedLabelClick(label)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleMixedLabelClick(label); }}
                                                        className={`quiz-chip text-sm ${Object.values(mixedSlotAnswers).some(v => v === label) ? 'quiz-chip-used' : ''}`}
                                                    >
                                                        {label}
                                                    </div>
                                                ))}
                                                {!showNextButton && Object.keys(mixedSlotAnswers).some(id => mixedDragSlots.some(s => s.id === id)) && (
                                                    <button type="button" onClick={handleMixedReset} className="quiz-secondary-btn !py-2 text-sm">Reset labels</button>
                                                )}
                                            </div>
                                        )}
                                        {!showNextButton && (
                                            <div className="mt-2">
                                                {mixedError && <p className="text-nova-coral font-medium text-sm">Fill in all fields before submitting.</p>}
                                                <button
                                                    type="button"
                                                    onClick={handleMixedSubmit}
                                                    disabled={!ButtonsAreClickable}
                                                    className="quiz-submit-btn focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                                                >
                                                    Submit
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    // Regular image question
                                    <img 
                                        src={quizQuestion.image} 
                                        alt="Question" 
                                        className="object-contain max-h-[60vh] max-w-full"
                                        draggable="false"
                                    />
                                )}
                            </CardContent>
                            </div>
                        </>
                    ) : (
                        // Case with no image - centered question text
                        <div className="min-h-[40vh] flex items-center justify-center"> {/* border border-black*/}
                            <h2 className="question-page-title text-2xl md:text-3xl font-bold text-center">
                                {quizQuestion.question}
                            </h2>
                        </div>
                    )}
                </Card>
                
                {streakAnimation && (
                    <img
                        src={streakAnimation}
                        className="streak-animation"
                    />
                )}       

                {showSparks && <SparksAddyBurst addyRef={addyRef} />}
                <div ref={addyRef} className="video-style-addy">
                    <AddyAvatar
                        equipped={showSparks ? sparks.equipped : {}}
                        catalogue={cosmeticCatalogue}
                        size="100%"
                        bleed
                        mood={addyTalking ? 'talking' : 'sway'}
                        celebrating={addyCelebrating}
                    />
                </div>
                
                {showFeedback && correctFeedback && (
                    <div className="feedback correct-feedback">
                        <p>Correct!</p>
                        {quizQuestion.correctFeedback && <p>{quizQuestion.correctFeedback}</p>}
                    </div>
                )}    

                {showFeedback && incorrectFeedback && (
                    <div className="feedback incorrect-feedback">
                        <p>Incorrect!</p>
                        {quizQuestion.incorrectFeedback && <p>{quizQuestion.incorrectFeedback}</p>}
                    </div>
                )}
                
            

            {quizQuestion.questionType === 'Multiple-Choice-Question' && (
                <div className="quiz-card p-6 w-full max-w-2xl mx-auto mt-4 max-md:p-4">
                    <div className="flex flex-col gap-3 answers-container-question-page">
                        {quizQuestion.answers.map((answer) => (
                            <button
                                key={answer.id}
                                type="button"
                                // Answered (Next showing): Quiz.js re-enables ButtonsAreClickable for the Next step, so the
                                // options must also stay locked or each re-click re-scores the question and grows the streak.
                                onClick={ButtonsAreClickable && !showNextButton ? () => handleButtonClick(answer) : undefined}
                                className={`quiz-option-card ${selectedAnswer === answer.id ? 'quiz-option-selected' : ''} ${!suppressLiveCorrectness && selectedAnswer === answer.id && answer.id === quizQuestion.correctAnswer ? '!border-nova-green !bg-nova-light-green' : ''} ${!suppressLiveCorrectness && selectedAnswer === answer.id && answer.id !== quizQuestion.correctAnswer ? '!border-nova-coral' : ''}`}
                            >
                                <span className="quiz-option-dot">
                                    {selectedAnswer === answer.id && <span className="quiz-option-dot-inner" />}
                                </span>
                                <span className="quiz-option-label">{answer.text}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {quizQuestion.questionType === 'Drop-Down-List' && (
                <main className="quiz-card p-6 w-full max-w-md mx-auto mt-4 drop-down-container-question-page max-md:p-4">
                    <div className="quiz-select-wrapper w-full my-2">
                        <select
                            value={selectedAnswer}
                            onChange={(e) => { 
                                setSelectedAnswer(e.target.value); 
                                onInputChange?.({
                                    questionType: 'Drop-Down-List',
                                    value: e.target.value
                                });        
                            }}
                            className={`quiz-select quiz-focus-ring w-full ${
                                showFeedback && correctFeedback ? 'quiz-input-correct' : ''
                            } ${showFeedback && incorrectFeedback ? 'quiz-input-incorrect' : ''}`}
                        >
                            <option value="select">Select Answer</option>
                            {quizQuestion.answers.map((answer) => (
                                <option key={answer.id} value={answer.id}>
                                    {answer.text}
                                </option>
                            ))}
                        </select>
                        <span className="quiz-select-chevron" aria-hidden>▾</span>
                    </div>
                    {!showNextButton && (
                        <button
                            type="button"
                            onClick={ButtonsAreClickable ? () => handleDropDownSubmit() : undefined}
                            className="quiz-submit-btn mt-4 focus:outline-none"
                        >
                            Submit
                        </button>
                    )}
                </main>
            )}

            {quizQuestion.questionType === 'Free-Text-Question' && (
                <main className="quiz-card p-6 w-full max-w-md mx-auto mt-4 free-text-container-question-page max-md:p-4">
                    <input
                        type="text"
                        value={FreeTextInputValue}
                        onChange={handleFreeTextChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.repeat && ButtonsAreClickable && !showNextButton) handleFreeTextSubmit(FreeTextInputValue);
                        }}
                        placeholder="Enter answer here"
                        className={`quiz-text-input quiz-focus-ring w-full max-w-md ${
                            showFeedback && correctFeedback ? 'quiz-input-correct' : ''
                        } ${showFeedback && incorrectFeedback ? 'quiz-input-incorrect quiz-input-shake' : ''} ${
                            freeTextError && !showFeedback ? '!border-nova-coral' : ''
                        }`}
                    />
                    {freeTextError && (
                        <div className="w-full text-center text-nova-coral font-medium mt-2 text-sm">
                            Please enter an answer before submitting.
                        </div>
                    )}
                    {!showNextButton && (
                        <button
                            type="button"
                            onClick={ButtonsAreClickable ? () => handleFreeTextSubmit(FreeTextInputValue) : undefined}
                            className="quiz-submit-btn mt-4 focus:outline-none"
                        >
                            Submit
                        </button>
                    )}
                </main>
            )}





            {showNextButton && (
                <div className="px-6 py-3 mb-4">
                    <button
                        type="button"
                        onClick={() => handleNextClick()}
                        className="quiz-submit-btn focus:outline-none"
                    >
                        {remainingQuestionsLength <= 1 ? 'Finish Quiz' : 'Next Question'}
                    </button>
                </div>
            )}
        { /* </div> */}
        </div>
    );
}

export default QuestionPage;
